
import { useState } from 'react';

const CommunitySection = () => {
  const [language, setLanguage] = useState('fr');
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleRegistrationSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const formData = new FormData(e.currentTarget);
    const data = new URLSearchParams();
    
    // Convertir FormData en URLSearchParams
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d45qo6ej3hkp5gme3sd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          setShowRegistrationModal(false);
          setSubmitStatus('idle');
          (e.target as HTMLFormElement).reset();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = {
    fr: {
      title: "Notre Communauté Maghrébine",
      subtitle: "Unis par la solidarité",
      description: "Découvrez les témoignages de nos membres qui s'entraident au quotidien",
      testimonials: [
        {
          name: "Amina B.",
          location: "Rouen Centre",
          service: "Aide informatique",
          text: "Grâce à Ahmed, j'ai pu installer mes logiciels en 1h. Un vrai dépannage express !",
          rating: 5,
          avatar: "https://public.readdy.ai/ai/img_res/0607c1131997558285d5dc42858cb190.jpg"
        },
        {
          name: "Karim M.",
          location: "Sotteville",
          service: "Réparation auto",
          text: "Ma voiture est tombée en panne, Youssef est venu en 30 minutes. Service parfait !",
          rating: 5,
          avatar: "https://readdy.ai/api/search-image?query=Portrait%20of%20smiling%20North%20African%20man%20in%20his%2030s%2C%20professional%20headshot%2C%20warm%20expression%2C%20casual%20shirt%2C%20natural%20lighting%2C%20friendly%20face&width=100&height=100&seq=avatar-karim&orientation=squarish"
        },
        {
          name: "Fatima L.",
          location: "Mont-Saint-Aignan",
          service: "Aide ménagère",
          text: "Samira m'a aidée pour mon déménagement. Une vraie sœur, merci Wald Bladi !",
          rating: 5,
          avatar: "https://readdy.ai/api/search-image?query=Portrait%20of%20smiling%20North%20African%20woman%20in%20her%2050s%2C%20professional%20headshot%2C%20warm%20expression%2C%20hijab%2C%20natural%20lighting%2C%20kind%20face&width=100&height=100&seq=avatar-fatima&orientation=squarish"
        }
      ],
      stats: [
        {
          icon: "ri-group-line",
          number: "200+",
          label: "Membres actifs",
          color: "text-orange-600"
        },
        {
          icon: "ri-time-line",
          number: "< 2h",
          label: "Temps de réponse",
          color: "text-green-600"
        },
        {
          icon: "ri-star-line",
          number: "4.9/5",
          label: "Satisfaction",
          color: "text-yellow-600"
        },
        {
          icon: "ri-map-pin-line",
          number: "15",
          label: "Quartiers couverts",
          color: "text-blue-600"
        }
      ],
      cta: {
        title: "Rejoignez notre communauté",
        description: "Ensemble, nous sommes plus forts. Aidez et soyez aidé !",
        button: "S'inscrire gratuitement"
      }
    },
    ar: {
      title: "مجتمعنا المغاربي",
      subtitle: "متحدون بالتضامن",
      description: "اكتشف شهادات أعضائنا الذين يساعدون بعضهم البعض يوميًا",
      testimonials: [
        {
          name: "أمينة ب.",
          location: "وسط روان",
          service: "مساعدة كمبيوتر",
          text: "بفضل أحمد، تمكنت من تثبيت برامجي في ساعة واحدة. إصلاح سريع حقيقي!",
          rating: 5,
          avatar: "https://readdy.ai/api/search-image?query=Portrait%20of%20smiling%20North%20African%20woman%20in%20her%2040s%2C%20professional%20headshot%2C%20warm%20expression%2C%20modern%20clothing%2C%20natural%20lighting%2C%20friendly%20face&width=100&height=100&seq=avatar-amina&orientation=squarish"
        },
        {
          name: "كريم م.",
          location: "سوتفيل",
          service: "إصلاح السيارة",
          text: "تعطلت سيارتي، جاء يوسف في 30 دقيقة. خدمة مثالية!",
          rating: 5,
          avatar: "https://readdy.ai/api/search-image?query=Portrait%20of%20smiling%20North%20African%20man%20in%20his%2030s%2C%20professional%20headshot%2C%20warm%20expression%2C%20casual%20shirt%2C%20natural%20lighting%2C%20friendly%20face&width=100&height=100&seq=avatar-karim&orientation=squarish"
        },
        {
          name: "فاطمة ل.",
          location: "مون سان إيغنان",
          service: "مساعدة منزلية",
          text: "ساعدتني سميرة في انتقالي. أخت حقيقية، شكراً ولد بلادي!",
          rating: 5,
          avatar: "https://readdy.ai/api/search-image?query=Portrait%20of%20smiling%20North%20African%20woman%20in%20her%2050s%2C%20professional%20headshot%2C%20warm%20expression%2C%20hijab%2C%20natural%20lighting%2C%20kind%20face&width=100&height=100&seq=avatar-fatima&orientation=squarish"
        }
      ],
      stats: [
        {
          icon: "ri-group-line",
          number: "200+",
          label: "عضو نشط",
          color: "text-orange-600"
        },
        {
          icon: "ri-time-line",
          number: "< 2 ساعة",
          label: "وقت الاستجابة",
          color: "text-green-600"
        },
        {
          icon: "ri-star-line",
          number: "4.9/5",
          label: "الرضا",
          color: "text-yellow-600"
        },
        {
          icon: "ri-map-pin-line",
          number: "15",
          label: "حي مغطى",
          color: "text-blue-600"
        }
      ],
      cta: {
        title: "انضم إلى مجتمعنا",
        description: "معاً، نحن أقوى. ساعد واحصل على المساعدة!",
        button: "التسجيل مجاناً"
      }
    }
  };

  const currentContent = content[language as keyof typeof content];

  return (
    <>
      <section className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Language Toggle */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setLanguage('fr')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  language === 'fr' 
                    ? 'bg-white text-orange-600 shadow-md' 
                    : 'text-gray-600 hover:text-orange-600'
                }`}
              >
                Français
              </button>
              <button
                onClick={() => setLanguage('ar')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  language === 'ar' 
                    ? 'bg-white text-orange-600 shadow-md' 
                    : 'text-gray-600 hover:text-orange-600'
                }`}
              >
                العربية
              </button>
            </div>
          </div>

          <div className={`text-center mb-16 ${language === 'ar' ? 'text-right' : 'text-left'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {currentContent.title}
            </h2>
            <p className="text-xl text-orange-600 font-semibold mb-4">
              {currentContent.subtitle}
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {currentContent.description}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {currentContent.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className={`w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100 ${stat.color}`}>
                  <i className={`${stat.icon} text-2xl`}></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {currentContent.testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center mb-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </div>
                
                <div className="flex items-center mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                  <span className="ml-2 text-sm text-orange-600 font-medium">{testimonial.service}</span>
                </div>
                
                <p className="text-gray-700 italic leading-relaxed">
                  "{testimonial.text}"
                </p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-3xl p-8 md:p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              {currentContent.cta.title}
            </h3>
            <p className="text-xl mb-8 opacity-90">
              {currentContent.cta.description}
            </p>
            <button className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap shadow-lg text-lg">
              <i className="ri-user-add-line mr-2"></i>
              {currentContent.cta.button}
            </button>
          </div>
        </div>
      </section>

      {/* CTA Button */}
      <div className="text-center">
        <button 
          onClick={() => setShowRegistrationModal(true)}
          className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap shadow-lg text-lg"
        >
          <i className="ri-user-add-line mr-2"></i>
          S'inscrire gratuitement
        </button>
      </div>

      {/* Modal d'inscription */}
      {showRegistrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    <i className="ri-user-add-line mr-3 text-orange-600"></i>
                    Rejoindre la communauté Wald Bladi
                  </h3>
                  <p className="text-gray-600">
                    Inscrivez-vous gratuitement pour accéder à tous nos services communautaires
                  </p>
                </div>
                <button
                  onClick={() => setShowRegistrationModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {/* Formulaire */}
              <form onSubmit={handleRegistrationSubmit} className="space-y-6" id="inscription-form" data-readdy-form>
                {/* Informations personnelles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="ri-user-line mr-2 text-orange-600"></i>
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="Votre prénom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="ri-user-line mr-2 text-orange-600"></i>
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="Votre nom"
                    />
                  </div>
                </div>

                {/* Contact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="ri-mail-line mr-2 text-orange-600"></i>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="ri-phone-line mr-2 text-orange-600"></i>
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>

                {/* Localisation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-map-pin-line mr-2 text-orange-600"></i>
                    Quartier/Secteur à Rouen *
                  </label>
                  <select
                    name="quartier"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Sélectionnez votre quartier</option>
                    <option value="centre-ville">Centre-ville</option>
                    <option value="rive-gauche">Rive Gauche</option>
                    <option value="mont-riboudet">Mont-Riboudet</option>
                    <option value="saint-sever">Saint-Sever</option>
                    <option value="grammont">Grammont</option>
                    <option value="sapins">Les Sapins</option>
                    <option value="chatelet">Châtelet</option>
                    <option value="autre">Autre secteur</option>
                  </select>
                </div>

                {/* Âge */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-calendar-line mr-2 text-orange-600"></i>
                    Tranche d'âge
                  </label>
                  <select
                    name="age"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Sélectionnez votre tranche d'âge</option>
                    <option value="18-25">18-25 ans</option>
                    <option value="26-35">26-35 ans</option>
                    <option value="36-45">36-45 ans</option>
                    <option value="46-55">46-55 ans</option>
                    <option value="56-65">56-65 ans</option>
                    <option value="65+">Plus de 65 ans</option>
                  </select>
                </div>

                {/* Centres d'intérêt */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-heart-line mr-2 text-orange-600"></i>
                    Centres d'intérêt (plusieurs choix possibles)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      'Aide administrative',
                      'Cours de langue',
                      'Activités culturelles',
                      'Sport et loisirs',
                      'Cuisine traditionnelle',
                      'Bricolage/Réparations',
                      "Garde d'enfants",
                      'Transport/Covoiturage',
                      'Informatique',
                      'Événements communautaires',
                      'Aide aux seniors',
                      'Jardinage'
                    ].map((interest) => (
                      <label key={interest} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="interets"
                          value={interest}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Motivation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-message-3-line mr-2 text-orange-600"></i>
                    Pourquoi souhaitez-vous rejoindre Wald Bladi ? (optionnel)
                  </label>
                  <textarea
                    name="motivation"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                    placeholder="Partagez vos motivations, vos attentes ou comment vous souhaitez contribuer à la communauté..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 caractères</p>
                </div>

                {/* Newsletter */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="newsletter"
                    value="oui"
                    id="newsletter"
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <label htmlFor="newsletter" className="text-sm text-gray-700 cursor-pointer">
                    Je souhaite recevoir la newsletter avec les actualités de la communauté
                  </label>
                </div>

                {/* Conditions */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    name="conditions"
                    value="accepte"
                    id="conditions"
                    required
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 mt-1"
                  />
                  <label htmlFor="conditions" className="text-sm text-gray-700 cursor-pointer">
                    J'accepte les conditions d'utilisation et la politique de confidentialité de Wald Bladi *
                  </label>
                </div>

                {/* Messages de statut */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-check-circle-line text-green-600 mr-2"></i>
                      <span className="text-green-800 font-medium">
                        Inscription réussie ! Bienvenue dans la communauté Wald Bladi !
                      </span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2"></i>
                      <span className="text-red-800 font-medium">
                        Erreur lors de l'inscription. Veuillez réessayer.
                      </span>
                    </div>
                  </div>
                )}

                {/* Boutons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowRegistrationModal(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-3 rounded-lg hover:from-orange-700 hover:to-red-700 transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Inscription en cours...
                      </>
                    ) : (
                      <>
                        <i className="ri-user-add-line mr-2"></i>
                        Rejoindre la communauté
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Info supplémentaire */}
              <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-orange-600 mt-1"></i>
                  <div className="text-sm text-orange-800">
                    <p className="font-medium mb-1">Après votre inscription :</p>
                    <ul className="space-y-1 text-orange-700">
                      <li>• Vous recevrez un email de confirmation</li>
                      <li>• Accès immédiat à tous nos services communautaires</li>
                      <li>• Possibilité de proposer et demander de l'aide</li>
                      <li>• Participation aux événements et activités</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommunitySection;
